import { User, Briefcase, MessageCircle } from 'lucide-react'

type Stakeholder = {
  id?: string
  name: string
  position?: string
  department?: string
  description?: string
  motivation?: string
  influence?: 'HIGH' | 'MEDIUM' | 'LOW'
  personality?: string[]
  communicationStyle?: string
  keyConcer?: string
  availability?: string
  relationshipToParticipant?: string
}

type StakeholderCardsProps = {
  stakeholders: unknown[]
}

function getInfluenceColor(influence: string | undefined) {
  switch (influence?.toUpperCase()) {
    case 'HIGH':
      return 'bg-red-100 text-red-700'
    case 'MEDIUM':
      return 'bg-yellow-100 text-yellow-700'
    case 'LOW':
      return 'bg-green-100 text-green-700'
    default:
      return 'bg-gray-100 text-gray-700'
  }
}

export function StakeholderCards({ stakeholders }: StakeholderCardsProps) {
  const typedStakeholders = stakeholders as Stakeholder[]

  if (typedStakeholders.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No stakeholders defined in this simulation
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
      {typedStakeholders.map((stakeholder, index) => (
        <div
          key={stakeholder.id || index}
          className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <User className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-gray-900 truncate">
                  {stakeholder.name}
                </h3>
                {stakeholder.position && (
                  <p className="text-sm text-gray-500 truncate">
                    {stakeholder.position}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="p-4 space-y-3">
            {/* Department */}
            {stakeholder.department && (
              <div className="flex items-center gap-2 text-sm">
                <Briefcase className="w-4 h-4 text-gray-400" />
                <span className="text-gray-600">{stakeholder.department}</span>
              </div>
            )}

            {/* Description */}
            {stakeholder.description && (
              <p className="text-sm text-gray-700">{stakeholder.description}</p>
            )}

            {/* Motivation */}
            {stakeholder.motivation && (
              <div>
                <span className="text-xs font-medium text-gray-500 uppercase">
                  Motivation
                </span>
                <p className="text-sm text-gray-700 mt-1">
                  {stakeholder.motivation}
                </p>
              </div>
            )}

            {/* Communication Style */}
            {stakeholder.communicationStyle && (
              <div className="flex items-start gap-2">
                <MessageCircle className="w-4 h-4 text-gray-400 mt-0.5" />
                <span className="text-sm text-gray-600">
                  {stakeholder.communicationStyle}
                </span>
              </div>
            )}

            {/* Personality tags */}
            {stakeholder.personality && stakeholder.personality.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {stakeholder.personality.map((trait, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-purple-100 text-purple-700"
                  >
                    {trait}
                  </span>
                ))}
              </div>
            )}

            {/* Influence badge */}
            {stakeholder.influence && (
              <div className="pt-2 border-t border-gray-100">
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getInfluenceColor(
                    stakeholder.influence
                  )}`}
                >
                  {stakeholder.influence} Influence
                </span>
              </div>
            )}

            {/* Relationship to participant */}
            {stakeholder.relationshipToParticipant && (
              <div className="text-xs text-gray-500">
                <span className="font-medium">Relationship:</span>{' '}
                {stakeholder.relationshipToParticipant}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}
